import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Shield, 
  LogOut, 
  Menu, 
  X, 
  ChevronDown, 
  Search, 
  QrCode, 
  Image as ImageIcon, 
  FileText, 
  Share2, 
  Video as Youtube, 
  Sparkles, 
  ArrowRight, 
  Settings, 
  Layers, 
  Clock, 
  TrendingUp,
  FileSpreadsheet,
  Link2,
  Lock,
  Camera,
  Barcode,
  Key,
  Compass,
  Trophy,
  Crop,
  FileCode,
  Activity,
  Volume2,
  Calculator,
  Pipette,
  LayoutGrid,
  Code,
  Bot,
  Tag,
  Hash,
  BarChart2,
  Gauge,
  Award,
  Globe,
  Calendar,
  Scale,
  DollarSign,
  Wifi,
  Timer,
  Dices
} from 'lucide-react';
import { onAuthStateChange, logoutAdmin } from '../services/auth';
import { getCategories } from '../services/db';
import logo from '../logo.png';

export const Header = () => {
  const [admin, setAdmin] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Mobile Accordion state
  const [mobileGeneratorsOpen, setMobileGeneratorsOpen] = useState(false);
  const [mobileImagesOpen, setMobileImagesOpen] = useState(false);
  const [mobilePdfsOpen, setMobilePdfsOpen] = useState(false);
  const [mobileAudioOpen, setMobileAudioOpen] = useState(false);
  const [mobileDevOpen, setMobileDevOpen] = useState(false);
  const [mobileFinancialOpen, setMobileFinancialOpen] = useState(false);
  const [mobileSocialOpen, setMobileSocialOpen] = useState(false);
  const [mobileSeoOpen, setMobileSeoOpen] = useState(false);
  const [mobileUtilityOpen, setMobileUtilityOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setAdmin(user);
    });

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    const fetchCategories = async () => {
      try {
        const loaded = await getCategories();
        setCategories(loaded);
      } catch (err) {
        console.error("Failed to load header categories:", err);
      }
    };

    window.addEventListener('scroll', handleScroll);
    fetchCategories();

    return () => {
      unsubscribe();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Sync Search Query state from URL changes
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setSearchQuery(queryParams.get('search') || '');
  }, [location.search]);

  const handleLogout = async () => {
    try {
      await logoutAdmin();
      setMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const isActive = (path) => location.pathname === path;
  const isCategoryActive = (categoryName) => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get('category') === categoryName;
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/');
    }
    setMenuOpen(false);
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (location.pathname === '/') {
      const newParams = new URLSearchParams(location.search);
      if (!val) {
        newParams.delete('search');
      } else {
        newParams.set('search', val);
      }
      navigate(`/?${newParams.toString()}`);
    }
  };

  // Helper helper to handle submenu item click
  const handleItemClick = (categoryName) => {
    navigate(`/?category=${encodeURIComponent(categoryName)}`);
    setMenuOpen(false);
  };

  return (
    <header 
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        transition: 'var(--transition-smooth)',
        background: scrolled ? 'rgba(255, 255, 255, 0.88)' : 'rgba(248, 250, 252, 0.95)',
        backdropFilter: 'blur(16px)',
        borderBottom: scrolled ? '1px solid var(--border-color)' : '1px solid rgba(15, 23, 42, 0.04)',
        padding: scrolled ? '10px 24px' : '16px 24px',
        boxShadow: scrolled ? '0 10px 30px -15px rgba(15, 23, 42, 0.05)' : 'none'
      }}
    >
      <div style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px'
      }}>
        {/* Left: Brand Logo */}
        <Link 
          to="/" 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            textDecoration: 'none',
            flexShrink: 0,
            gap: '8px'
          }}
          className="brand-logo-link"
        >
          <img 
            src={logo} 
            alt="Toolmetrico Logo"
            width="32"
            height="32"
            style={{ 
              height: '32px', 
              width: 'auto', 
              objectFit: 'contain',
              display: 'block',
              transition: 'transform 0.3s'
            }} 
            className="logo-img"
          />
        </Link>

        {/* Center: Desktop Premium Navigation */}
        <nav style={{ display: 'none', alignItems: 'center', gap: '8px' }} className="desktop-nav">
          <Link 
            to="/" 
            style={{
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 600,
              padding: '8px 14px',
              borderRadius: '8px',
              color: isActive('/') && !location.search ? 'var(--primary)' : 'var(--text-secondary)',
              background: isActive('/') && !location.search ? 'rgba(5, 117, 251, 0.06)' : 'transparent',
              transition: 'var(--transition-smooth)'
            }}
            className="nav-link-item"
          >
            Home
          </Link>
          <Link 
            to="/blog" 
            style={{ 
              textDecoration: 'none', 
              color: location.pathname.includes('/blog') ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: 600,
              fontSize: '0.9rem',
              padding: '8px 14px',
              borderRadius: '8px',
              background: location.pathname.includes('/blog') ? 'rgba(5, 117, 251, 0.08)' : 'transparent',
              transition: 'var(--transition-smooth)'
            }}
            className="nav-link-item"
          >
            Blog
          </Link>

          {/* 1. QR & Generator Tools Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('QR & Generator Tools') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              QR & Generator Tools <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel">
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">Popular Tools</div>
                  <button onClick={() => { navigate('/qrcode-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><QrCode size={16} /></div>
                    <div>
                      <div className="dropdown-title">QR Code Generator</div>
                      <div className="dropdown-desc">Design custom, branded, dynamic QR codes</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/qr-scanner'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Camera size={16} /></div>
                    <div>
                      <div className="dropdown-title">QR Code Scanner</div>
                      <div className="dropdown-desc">Scan QR codes via camera or image file uploads</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/barcode-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Barcode size={16} /></div>
                    <div>
                      <div className="dropdown-title">Barcode Generator</div>
                      <div className="dropdown-desc">Create high-resolution vector Code 39 barcodes</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/key-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Key size={16} /></div>
                    <div>
                      <div className="dropdown-title">Sleek Key & Password Creator</div>
                      <div className="dropdown-desc">Generate secure passwords, hashes, & keys</div>
                    </div>
                  </button>
                </div>
                <div className="dropdown-featured-card">
                  <div className="featured-badge"><Sparkles size={12} /> Live Creator</div>
                  <h4>Instant Generator</h4>
                  <p>Create free downloadable code files and formatted keys in seconds.</p>
                  <button onClick={() => handleItemClick('QR & Generator Tools')} className="featured-action">
                    Explore QR Tools <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Image Tools Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('Image Tools') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              Image Tools <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel">
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">Asset Optimization</div>
                  <button onClick={() => { navigate('/image-compressor'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><ImageIcon size={16} /></div>
                    <div>
                      <div className="dropdown-title">Image Compressor</div>
                      <div className="dropdown-desc">Shrink WebP, PNG, & JPG sizes dynamically</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/remove-background'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Layers size={16} /></div>
                    <div>
                      <div className="dropdown-title">Remove Background</div>
                      <div className="dropdown-desc">Key out white, black, or custom sample colors</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/image-workshop'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Crop size={16} /></div>
                    <div>
                      <div className="dropdown-title">Resizer & Cropper</div>
                      <div className="dropdown-desc">Resize pixel bounds or crop visual custom layouts</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/gradient-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Compass size={16} /></div>
                    <div>
                      <div className="dropdown-title">CSS Gradient Generator</div>
                      <div className="dropdown-desc">Design gorgeous gradients & download 4K wall papers</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/format-converter'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Settings size={16} /></div>
                    <div>
                      <div className="dropdown-title">Format Converter</div>
                      <div className="dropdown-desc">Convert images, PDFs, vectors and Base64 layers</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/image-color-picker'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Pipette size={16} /></div>
                    <div>
                      <div className="dropdown-title">Image Color Picker</div>
                      <div className="dropdown-desc">Extract exact HEX, RGB, & HSL color palettes</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/photo-collage'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><LayoutGrid size={16} /></div>
                    <div>
                      <div className="dropdown-title">Photo Collage Maker</div>
                      <div className="dropdown-desc">Combine multiple photos into beautiful grid layouts</div>
                    </div>
                  </button>
                </div>
                <div className="dropdown-featured-card secondary">
                  <div className="featured-badge" style={{ background: 'rgba(14, 165, 233, 0.15)', color: 'var(--secondary)' }}><TrendingUp size={12} /> SEO Vital</div>
                  <h4>Speed Up Pages</h4>
                  <p>Optimized image files improve UX and maximize your site load speeds.</p>
                  <button onClick={() => handleItemClick('Image Tools')} className="featured-action secondary">
                    View Image Tools <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 3. PDF Tools Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('PDF Tools') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              PDF Tools <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel">
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">Document Hub</div>
                  <button onClick={() => { navigate('/pdf-utilities'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><FileText size={16} /></div>
                    <div>
                      <div className="dropdown-title">PDF Utilities</div>
                      <div className="dropdown-desc">Merge, split, protect, and compress sheets</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/pdf-utilities'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><FileSpreadsheet size={16} /></div>
                    <div>
                      <div className="dropdown-title">Format Converters</div>
                      <div className="dropdown-desc">Convert PDF files to Word, JPG, Excel, or HTML</div>
                    </div>
                  </button>
                </div>
                <div className="dropdown-featured-card pdf">
                  <div className="featured-badge" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}><Lock size={12} /> Secured</div>
                  <h4>Secure Processing</h4>
                  <p>All file transformations run client-side for maximum cloud security.</p>
                  <button onClick={() => { navigate('/pdf-utilities'); setMenuOpen(false); }} className="featured-action pdf">
                    View PDF Tools <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 3.5. Audio Tools Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('Audio Tools') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              Audio Tools <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel">
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">DSP Studio</div>
                  <button onClick={() => { navigate('/audio-workshop'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#a855f7', background: 'rgba(168, 85, 247, 0.05)' }}><Activity size={16} /></div>
                    <div>
                      <div className="dropdown-title">Audio Workshop</div>
                      <div className="dropdown-desc">Cut, compress, boost bass & speed tempo</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/text-to-speech'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#f43f5e', background: 'rgba(244, 63, 94, 0.05)' }}><Volume2 size={16} /></div>
                    <div>
                      <div className="dropdown-title">Text to Speech</div>
                      <div className="dropdown-desc">AI Vocalist synthesizer and speed controller</div>
                    </div>
                  </button>
                </div>
                <div className="dropdown-featured-card" style={{ borderLeft: '3px solid #a855f7' }}>
                  <div className="featured-badge" style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}><Sparkles size={12} /> High Fidelity</div>
                  <h4>Web Audio DSP</h4>
                  <p>Process your sound waves natively inside local thread contexts.</p>
                  <button onClick={() => { navigate('/audio-workshop'); setMenuOpen(false); }} className="featured-action" style={{ color: '#a855f7' }}>
                    Launch Studio <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 3.8. Developer Tools Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('Developer Tools') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              Developer Tools <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel" style={{ minWidth: '320px' }}>
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">Optimization Engine</div>
                  <button onClick={() => { navigate('/code-minifier'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: 'var(--primary)', background: 'rgba(5, 117, 251, 0.05)' }}><FileCode size={16} /></div>
                    <div>
                      <div className="dropdown-title">Code Minifier</div>
                      <div className="dropdown-desc">Compress and collapse HTML, CSS, and JS structures</div>
                    </div>
                  </button>
                </div>
                <div className="dropdown-featured-card" style={{ borderLeft: '3px solid var(--primary)' }}>
                  <div className="featured-badge" style={{ background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)' }}><Sparkles size={12} /> High Efficiency</div>
                  <h4>Speed Optimization</h4>
                  <p>Reduce standard code footprint bytes client-side to maximize loading speeds.</p>
                  <button onClick={() => { navigate('/code-minifier'); setMenuOpen(false); }} className="featured-action" style={{ color: 'var(--primary)' }}>
                    Minify Code <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 3.9. Financial Tools Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('Financial Tools') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              Financial Tools <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel" style={{ minWidth: '320px' }}>
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">Calculators</div>
                  <button onClick={() => { navigate('/loan-calculator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#059669', background: 'rgba(5, 150, 105, 0.05)' }}><Calculator size={16} /></div>
                    <div>
                      <div className="dropdown-title">Loan Calculator</div>
                      <div className="dropdown-desc">Estimate dynamic principal interests schedule payments</div>
                    </div>
                  </button>
                </div>
                <div className="dropdown-featured-card" style={{ borderLeft: '3px solid #059669' }}>
                  <div className="featured-badge" style={{ background: 'rgba(5, 150, 105, 0.1)', color: '#059669' }}><Sparkles size={12} /> Fast Financials</div>
                  <h4>Mortgage Planner</h4>
                  <p>Repay your mortgage faster by calculating the exact timeline impact of extra payments.</p>
                  <button onClick={() => { navigate('/loan-calculator'); setMenuOpen(false); }} className="featured-action" style={{ color: '#059669' }}>
                    Plan Loan <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* SEO & Marketing Tools Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('SEO & Marketing Tools') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              SEO Tools <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel" style={{ minWidth: '340px' }}>
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">Search Optimization</div>
                  <button onClick={() => { navigate('/meta-tag-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}><Code size={16} /></div>
                    <div>
                      <div className="dropdown-title">Meta Tag Generator</div>
                      <div className="dropdown-desc">HTML SERP preview tags</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/open-graph-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#0284c7', background: 'rgba(2, 132, 199, 0.08)' }}><Share2 size={16} /></div>
                    <div>
                      <div className="dropdown-title">Open Graph Generator</div>
                      <div className="dropdown-desc">Social share preview cards</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/robots-txt-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}><Bot size={16} /></div>
                    <div>
                      <div className="dropdown-title">Robots.txt Generator</div>
                      <div className="dropdown-desc">Crawler spider directives</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/sitemap-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#f59e0b', background: 'rgba(245, 158, 11, 0.08)' }}><FileCode size={16} /></div>
                    <div>
                      <div className="dropdown-title">XML Sitemap Generator</div>
                      <div className="dropdown-desc">GSC index map builder</div>
                    </div>
                  </button>
                  <div className="dropdown-list-header" style={{ marginTop: '8px' }}>Content & Analysis</div>
                  <button onClick={() => { navigate('/hashtag-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#0284c7', background: 'rgba(2, 132, 199, 0.08)' }}><Hash size={16} /></div>
                    <div>
                      <div className="dropdown-title">Viral Hashtag Generator</div>
                      <div className="dropdown-desc">Instagram & TikTok reach tags</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/keyword-density-checker'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}><BarChart2 size={16} /></div>
                    <div>
                      <div className="dropdown-title">Keyword Density Checker</div>
                      <div className="dropdown-desc">Stuffing & n-gram analyzer</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/seo-analyzer'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}><Gauge size={16} /></div>
                    <div>
                      <div className="dropdown-title">SEO Analyzer</div>
                      <div className="dropdown-desc">On-page audit score check</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/backlink-checker'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#0284c7', background: 'rgba(2, 132, 199, 0.08)' }}><Award size={16} /></div>
                    <div>
                      <div className="dropdown-title">Backlink Checker</div>
                      <div className="dropdown-desc">Domain authority profiler</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/google-index-checker'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}><Globe size={16} /></div>
                    <div>
                      <div className="dropdown-title">Google Index Checker</div>
                      <div className="dropdown-desc">SERP operator verification</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Utility Tools Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('Utility Tools') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              Utility Tools <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel" style={{ minWidth: '340px' }}>
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">Calculators & Converters</div>
                  <button onClick={() => { navigate('/age-calculator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}><Calendar size={16} /></div>
                    <div>
                      <div className="dropdown-title">Exact Age Calculator</div>
                      <div className="dropdown-desc">Chronological age & birthday tracker</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/bmi-calculator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#0284c7', background: 'rgba(2, 132, 199, 0.08)' }}><Activity size={16} /></div>
                    <div>
                      <div className="dropdown-title">BMI Calculator</div>
                      <div className="dropdown-desc">Body mass index & BMR estimation</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/currency-converter'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#f59e0b', background: 'rgba(245, 158, 11, 0.08)' }}><DollarSign size={16} /></div>
                    <div>
                      <div className="dropdown-title">Live Currency Converter</div>
                      <div className="dropdown-desc">Global exchange rate parity</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/unit-converter'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.08)' }}><Scale size={16} /></div>
                    <div>
                      <div className="dropdown-title">Universal Unit Converter</div>
                      <div className="dropdown-desc">Length, mass, area & temperature</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/time-zone-converter'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#ec4899', background: 'rgba(236, 72, 153, 0.08)' }}><Clock size={16} /></div>
                    <div>
                      <div className="dropdown-title">Time Zone Converter</div>
                      <div className="dropdown-desc">International meeting planner</div>
                    </div>
                  </button>
                  <div className="dropdown-list-header" style={{ marginTop: '8px' }}>Timers & Fun Tools</div>
                  <button onClick={() => { navigate('/internet-speed-test'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}><Wifi size={16} /></div>
                    <div>
                      <div className="dropdown-title">Internet Speed Test</div>
                      <div className="dropdown-desc">Bandwidth & ping latency check</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/stopwatch'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#ef4444', background: 'rgba(239, 68, 68, 0.08)' }}><Clock size={16} /></div>
                    <div>
                      <div className="dropdown-title">Precision Stopwatch</div>
                      <div className="dropdown-desc">Millisecond split lap logging</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/countdown-timer'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#f59e0b', background: 'rgba(245, 158, 11, 0.08)' }}><Timer size={16} /></div>
                    <div>
                      <div className="dropdown-title">Countdown Timer</div>
                      <div className="dropdown-desc">Pomodoro alarms & interval breaks</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/random-picker-wheel'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#06b6d4', background: 'rgba(6, 182, 212, 0.08)' }}><Dices size={16} /></div>
                    <div>
                      <div className="dropdown-title">Random Picker Wheel</div>
                      <div className="dropdown-desc">Animated raffle spinner</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/calendar-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}><Calendar size={16} /></div>
                    <div>
                      <div className="dropdown-title">Calendar Generator</div>
                      <div className="dropdown-desc">Printable grid view & event notes</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Social Media Dropdown */}
          <div className="nav-dropdown-wrapper">
            <button
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isCategoryActive('YouTube Tools') || isCategoryActive('Social Media') ? 'var(--primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                padding: '8px 14px',
                borderRadius: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="nav-dropdown-trigger"
            >
              Social Media <ChevronDown size={14} className="arrow-down-icon" />
            </button>
            
            <div className="dropdown-menu-panel" style={{ minWidth: '320px' }}>
              <div className="dropdown-grid">
                <div className="dropdown-links-list">
                  <div className="dropdown-list-header">YouTube Tools</div>
                  <button onClick={() => { navigate('/youtube-video-downloader'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444' }}><Youtube size={16} /></div>
                    <div>
                      <div className="dropdown-title" style={{ color: '#ef4444' }}>Video Downloader</div>
                      <div className="dropdown-desc">Download YouTube MP4 videos & MP3 audio tracks</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/youtube-downloader'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444' }}><ImageIcon size={16} /></div>
                    <div>
                      <div className="dropdown-title" style={{ color: '#ef4444' }}>Thumbnail Downloader</div>
                      <div className="dropdown-desc">Extract high-res YouTube video cover image assets</div>
                    </div>
                  </button>
                  <button onClick={() => { navigate('/youtube-title-generator'); setMenuOpen(false); }} className="dropdown-link">
                    <div className="dropdown-icon-wrapper" style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444' }}><Trophy size={16} /></div>
                    <div>
                      <div className="dropdown-title" style={{ color: '#ef4444' }}>Title Generator</div>
                      <div className="dropdown-desc">Catchy titles with length truncation gauges</div>
                    </div>
                  </button>
                  <div className="dropdown-list-header" style={{ marginTop: '8px' }}>Other Social Channels</div>
                  <button onClick={() => handleItemClick('YouTube Tools')} className="dropdown-link">
                    <div className="dropdown-icon-wrapper"><Share2 size={16} /></div>
                    <div>
                      <div className="dropdown-title">Instagram & TikTok</div>
                      <div className="dropdown-desc">Generate bios, tags, and engagement triggers</div>
                    </div>
                  </button>
                </div>
                <div className="dropdown-featured-card social">
                  <div className="featured-badge" style={{ background: 'rgba(224, 242, 254, 0.8)', color: '#0369a1' }}><TrendingUp size={12} /> Viral Suite</div>
                  <h4>Audience Booster</h4>
                  <p>Expand organic social reach using data-backed creators.</p>
                  <button onClick={() => handleItemClick('YouTube Tools')} className="featured-action social">
                    Launch YouTube Suite <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>

        </nav>

        {/* Right side: Search Bar + Admin Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 0 }}>
          
          {/* Desktop Admin panel triggers */}
          {admin && (
            <div style={{ display: 'none', alignItems: 'center', gap: '12px' }} className="desktop-admin-controls">
              <Link 
                to="/admin/dashboard" 
                style={{
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: isActive('/admin/dashboard') ? '#ffffff' : 'var(--text-primary)',
                  background: isActive('/admin/dashboard') ? 'var(--primary)' : 'rgba(15, 23, 42, 0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  transition: 'var(--transition-smooth)'
                }}
                className="admin-dashboard-btn"
              >
                <Shield size={14} /> Dashboard
              </Link>
              <button 
                onClick={handleLogout}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  transition: 'var(--transition-smooth)'
                }}
                className="admin-logout-btn"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}

          {/* Mobile Toggle Hamburger Button */}
          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              background: 'rgba(15, 23, 42, 0.04)',
              border: 'none',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              display: 'flex',
              width: '38px',
              height: '38px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'var(--transition-smooth)'
            }}
            className="mobile-nav-toggle"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {menuOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-color)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.12)',
          animation: 'fadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards',
          maxHeight: 'calc(100vh - 80px)',
          overflowY: 'auto'
        }}>


          {/* Mobile Core Pages Links */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Link 
              to="/" 
              onClick={() => setMenuOpen(false)}
              style={{
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '10px 12px',
                borderRadius: '8px',
                color: isActive('/') && !location.search ? 'var(--primary)' : 'var(--text-primary)',
                background: isActive('/') && !location.search ? 'rgba(5, 117, 251, 0.05)' : 'transparent'
              }}
            >
              Home Directory
            </Link>

            {/* Mobile QR & Generators Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobileGeneratorsOpen(!mobileGeneratorsOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><QrCode size={18} style={{ color: 'var(--primary)' }} /> QR & Generator Tools</span>
                <ChevronDown size={16} style={{ transform: mobileGeneratorsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobileGeneratorsOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(5, 117, 251, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/qrcode-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    QR Code Generator
                  </button>
                  <button onClick={() => { navigate('/qr-scanner'); setMenuOpen(false); }} className="mobile-sub-link">
                    QR Code Scanner
                  </button>
                  <button onClick={() => { navigate('/barcode-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Barcode Generator
                  </button>
                  <button onClick={() => { navigate('/key-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Key & Password Creator
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Image Tools Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobileImagesOpen(!mobileImagesOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><ImageIcon size={18} style={{ color: 'var(--secondary)' }} /> Image Tools</span>
                <ChevronDown size={16} style={{ transform: mobileImagesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobileImagesOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(14, 165, 233, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/image-compressor'); setMenuOpen(false); }} className="mobile-sub-link">
                    Image Compressor
                  </button>
                  <button onClick={() => { navigate('/remove-background'); setMenuOpen(false); }} className="mobile-sub-link">
                    Remove Background
                  </button>
                  <button onClick={() => { navigate('/image-workshop'); setMenuOpen(false); }} className="mobile-sub-link">
                    Resizer & Cropper
                  </button>
                  <button onClick={() => { navigate('/gradient-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Gradient Generator
                  </button>
                  <button onClick={() => { navigate('/format-converter'); setMenuOpen(false); }} className="mobile-sub-link">
                    Format Converter
                  </button>
                  <button onClick={() => { navigate('/image-color-picker'); setMenuOpen(false); }} className="mobile-sub-link">
                    Image Color Picker
                  </button>
                  <button onClick={() => { navigate('/photo-collage'); setMenuOpen(false); }} className="mobile-sub-link">
                    Photo Collage Maker
                  </button>
                </div>
              )}
            </div>

            {/* Mobile PDF Tools Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobilePdfsOpen(!mobilePdfsOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FileText size={18} style={{ color: '#ef4444' }} /> PDF Tools</span>
                <ChevronDown size={16} style={{ transform: mobilePdfsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobilePdfsOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(239, 68, 68, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/pdf-utilities'); setMenuOpen(false); }} className="mobile-sub-link">
                    PDF Utilities Dashboard
                  </button>
                  <button onClick={() => { navigate('/pdf-utilities'); setMenuOpen(false); }} className="mobile-sub-link">
                    Format & Office PDF Converters
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Audio Tools Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobileAudioOpen(!mobileAudioOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Activity size={18} style={{ color: '#a855f7' }} /> Audio Tools</span>
                <ChevronDown size={16} style={{ transform: mobileAudioOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobileAudioOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(168, 85, 247, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/audio-workshop'); setMenuOpen(false); }} className="mobile-sub-link">
                    Audio Processing Workshop
                  </button>
                  <button onClick={() => { navigate('/text-to-speech'); setMenuOpen(false); }} className="mobile-sub-link">
                    Text to Speech Studio
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Developer Tools Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobileDevOpen(!mobileDevOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FileCode size={18} style={{ color: 'var(--primary)' }} /> Developer Tools</span>
                <ChevronDown size={16} style={{ transform: mobileDevOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobileDevOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(5, 117, 251, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/code-minifier'); setMenuOpen(false); }} className="mobile-sub-link">
                    Code Minifier Workspace
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Financial Tools Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobileFinancialOpen(!mobileFinancialOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Calculator size={18} style={{ color: '#059669' }} /> Financial Tools</span>
                <ChevronDown size={16} style={{ transform: mobileFinancialOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobileFinancialOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(5, 150, 105, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/loan-calculator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Loan & Mortgage Planner
                  </button>
                </div>
              )}
            </div>

            {/* Mobile SEO & Marketing Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobileSeoOpen(!mobileSeoOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Gauge size={18} style={{ color: '#10b981' }} /> SEO & Marketing</span>
                <ChevronDown size={16} style={{ transform: mobileSeoOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobileSeoOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(16, 185, 129, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/meta-tag-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Meta Tag Generator
                  </button>
                  <button onClick={() => { navigate('/open-graph-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Open Graph Generator
                  </button>
                  <button onClick={() => { navigate('/robots-txt-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Robots.txt Generator
                  </button>
                  <button onClick={() => { navigate('/sitemap-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    XML Sitemap Generator
                  </button>
                  <button onClick={() => { navigate('/hashtag-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Viral Hashtag Generator
                  </button>
                  <button onClick={() => { navigate('/keyword-density-checker'); setMenuOpen(false); }} className="mobile-sub-link">
                    Keyword Density Checker
                  </button>
                  <button onClick={() => { navigate('/seo-analyzer'); setMenuOpen(false); }} className="mobile-sub-link">
                    SEO Analyzer
                  </button>
                  <button onClick={() => { navigate('/backlink-checker'); setMenuOpen(false); }} className="mobile-sub-link">
                    Backlink Checker
                  </button>
                  <button onClick={() => { navigate('/google-index-checker'); setMenuOpen(false); }} className="mobile-sub-link">
                    Google Index Checker
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Utility Tools Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobileUtilityOpen(!mobileUtilityOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Scale size={18} style={{ color: '#8b5cf6' }} /> Utility Tools</span>
                <ChevronDown size={16} style={{ transform: mobileUtilityOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobileUtilityOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(139, 92, 246, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/age-calculator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Exact Age Calculator
                  </button>
                  <button onClick={() => { navigate('/bmi-calculator'); setMenuOpen(false); }} className="mobile-sub-link">
                    BMI Calculator
                  </button>
                  <button onClick={() => { navigate('/currency-converter'); setMenuOpen(false); }} className="mobile-sub-link">
                    Live Currency Converter
                  </button>
                  <button onClick={() => { navigate('/unit-converter'); setMenuOpen(false); }} className="mobile-sub-link">
                    Universal Unit Converter
                  </button>
                  <button onClick={() => { navigate('/time-zone-converter'); setMenuOpen(false); }} className="mobile-sub-link">
                    Time Zone Converter
                  </button>
                  <button onClick={() => { navigate('/internet-speed-test'); setMenuOpen(false); }} className="mobile-sub-link">
                    Internet Speed Test
                  </button>
                  <button onClick={() => { navigate('/stopwatch'); setMenuOpen(false); }} className="mobile-sub-link">
                    Precision Stopwatch
                  </button>
                  <button onClick={() => { navigate('/countdown-timer'); setMenuOpen(false); }} className="mobile-sub-link">
                    Countdown Timer
                  </button>
                  <button onClick={() => { navigate('/random-picker-wheel'); setMenuOpen(false); }} className="mobile-sub-link">
                    Random Picker Wheel
                  </button>
                  <button onClick={() => { navigate('/calendar-generator'); setMenuOpen(false); }} className="mobile-sub-link">
                    Calendar Generator
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Social Media Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button 
                onClick={() => setMobileSocialOpen(!mobileSocialOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Share2 size={18} style={{ color: '#0284c7' }} /> Social Media</span>
                <ChevronDown size={16} style={{ transform: mobileSocialOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {mobileSocialOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '28px', borderLeft: '2px solid rgba(2, 132, 199, 0.2)', marginLeft: '20px', marginBlock: '4px' }}>
                  <button onClick={() => { navigate('/youtube-video-downloader'); setMenuOpen(false); }} className="mobile-sub-link" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', fontWeight: 600 }}>
                    <Youtube size={14} /> Video Downloader
                  </button>
                  <button onClick={() => { navigate('/youtube-downloader'); setMenuOpen(false); }} className="mobile-sub-link" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', fontWeight: 600 }}>
                    <ImageIcon size={14} /> Thumbnail Downloader
                  </button>
                  <button onClick={() => { navigate('/youtube-title-generator'); setMenuOpen(false); }} className="mobile-sub-link" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', fontWeight: 600 }}>
                    <Trophy size={14} /> Title Generator
                  </button>
                  <button onClick={() => handleItemClick('YouTube Tools')} className="mobile-sub-link">
                    Instagram & TikTok Systems
                  </button>
                </div>
              )}
            </div>

          </div>

          {/* Mobile Admin panel actions */}
          {admin && (
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link 
                to="/admin/dashboard" 
                onClick={() => setMenuOpen(false)}
                style={{
                  textDecoration: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  padding: '10px 12px',
                  borderRadius: '8px',
                  color: isActive('/admin/dashboard') ? '#ffffff' : 'var(--text-primary)',
                  background: isActive('/admin/dashboard') ? 'var(--primary)' : 'rgba(15, 23, 42, 0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Shield size={16} /> Admin Dashboard
              </Link>
              <button 
                onClick={handleLogout}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  textAlign: 'left',
                  width: '100%'
                }}
              >
                <LogOut size={16} /> Logout Session
              </button>
            </div>
          )}
        </div>
      )}

      {/* Embedded High-Fidelity CSS styling for premiums header */}
      <style>{`
        @media (min-width: 992px) {
          .desktop-nav {
            display: flex !important;
          }
          .desktop-search-form {
            display: flex !important;
          }
          .desktop-admin-controls {
            display: flex !important;
          }
          .mobile-nav-toggle {
            display: none !important;
          }
        }
        
        .brand-logo-link:hover .logo-img {
          transform: scale(1.05);
        }
        
        .header-search-input {
          transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s, box-shadow 0.2s !important;
        }
        
        .header-search-input:focus {
          width: 240px !important;
          outline: none;
          border-color: var(--primary) !important;
          box-shadow: 0 0 0 3px rgba(5, 117, 251, 0.12) !important;
        }

        /* Desktop Dropdown CSS Systems */
        .nav-dropdown-wrapper {
          position: relative;
        }

        .nav-dropdown-trigger {
          transition: background-color 0.2s, color 0.2s;
        }

        .nav-dropdown-trigger:hover, .nav-link-item:hover {
          color: var(--primary) !important;
          background-color: rgba(5, 117, 251, 0.04) !important;
        }

        .arrow-down-icon {
          transition: transform 0.2s ease;
        }

        .nav-dropdown-wrapper:hover .arrow-down-icon {
          transform: rotate(180deg);
        }

        .dropdown-menu-panel {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%) translateY(12px);
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 16px;
          box-shadow: 0 20px 40px -15px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.02);
          padding: 8px;
          min-width: 460px;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.25s cubic-bezier(0.16, 1, 0.3, 1), transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.25s;
          pointer-events: none;
        }

        /* Dropdown open on Hover */
        .nav-dropdown-wrapper:hover .dropdown-menu-panel {
          opacity: 1;
          visibility: visible;
          transform: translateX(-50%) translateY(0);
          pointer-events: auto;
        }

        .dropdown-grid {
          display: grid;
          grid-template-columns: 1.3fr 1fr;
          gap: 12px;
        }

        .dropdown-links-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 8px;
        }

        .dropdown-list-header {
          font-size: 0.725rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--text-muted);
          padding-left: 12px;
          margin-bottom: 6px;
        }

        .dropdown-link {
          background: transparent;
          border: none;
          text-align: left;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 10px;
          text-decoration: none;
          cursor: pointer;
          transition: background-color 0.2s, transform 0.2s;
        }

        .dropdown-link:hover {
          background-color: rgba(5, 117, 251, 0.05);
          transform: translateX(3px);
        }

        .dropdown-icon-wrapper {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: rgba(5, 117, 251, 0.06);
          color: var(--primary);
          flex-shrink: 0;
          transition: transform 0.2s;
        }

        .dropdown-link:hover .dropdown-icon-wrapper {
          transform: scale(1.05);
        }

        .dropdown-title {
          font-size: 0.85rem;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.2;
          margin-bottom: 2px;
        }

        .dropdown-desc {
          font-size: 0.725rem;
          color: var(--text-secondary);
          line-height: 1.45;
        }

        /* Featured Promotion Cards inside Dropdowns */
        .dropdown-featured-card {
          background: linear-gradient(135deg, rgba(5, 117, 251, 0.04) 0%, rgba(14, 165, 233, 0.04) 100%);
          border: 1px solid rgba(5, 117, 251, 0.08);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .dropdown-featured-card.secondary {
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.04) 0%, rgba(6, 182, 212, 0.04) 100%);
          border-color: rgba(14, 165, 233, 0.08);
        }

        .dropdown-featured-card.pdf {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.03) 0%, rgba(244, 63, 94, 0.03) 100%);
          border-color: rgba(239, 68, 68, 0.06);
        }

        .dropdown-featured-card.social {
          background: linear-gradient(135deg, rgba(2, 132, 199, 0.03) 0%, rgba(14, 165, 233, 0.03) 100%);
          border-color: rgba(2, 132, 199, 0.06);
        }

        .featured-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          align-self: flex-start;
          background: rgba(5, 117, 251, 0.12);
          color: var(--primary);
          font-size: 0.65rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 3px 8px;
          border-radius: 99px;
          margin-bottom: 12px;
        }

        .dropdown-featured-card h4 {
          font-size: 0.9rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 6px;
        }

        .dropdown-featured-card p {
          font-size: 0.725rem;
          color: var(--text-secondary);
          line-height: 1.45;
          margin-bottom: 14px;
        }

        .featured-action {
          align-self: flex-start;
          background: var(--primary);
          color: #ffffff;
          border: none;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 6px 12px;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          transition: background-color 0.2s, transform 0.2s;
        }

        .featured-action:hover {
          background: var(--primary-hover);
          transform: translateY(-1px);
        }

        .featured-action.secondary {
          background: var(--secondary);
        }
        .featured-action.secondary:hover {
          background: #0284c7;
        }

        .featured-action.pdf {
          background: #ef4444;
        }
        .featured-action.pdf:hover {
          background: #dc2626;
        }

        .featured-action.social {
          background: #0284c7;
        }
        .featured-action.social:hover {
          background: #0369a1;
        }

        /* Mobile sub-navigation link */
        .mobile-sub-link {
          background: transparent;
          border: none;
          text-align: left;
          padding: 8px 12px;
          font-size: 0.85rem;
          color: var(--text-secondary);
          cursor: pointer;
          transition: color 0.2s, padding-left 0.2s;
        }

        .mobile-sub-link:hover {
          color: var(--primary);
          padding-left: 16px;
        }

        /* Admin panel interactive button custom hover adjustments */
        .admin-dashboard-btn:hover {
          opacity: 0.92;
          transform: translateY(-1px);
        }

        .admin-logout-btn:hover {
          background: rgba(239, 68, 68, 0.05);
          transform: translateY(-1px);
        }
      `}</style>
    </header>
  );
};

export default Header;
